import { useState, type ChangeEvent, type ComponentType, type DragEvent } from 'react';

interface FileDropZoneProps {
  /** Accessible name of the file input. */
  label: string;
  /** What to drag, as read in "Drag and drop {noun} here". */
  noun: string;
  /** Shows at a glance which kind of file this zone is for. */
  icon: ComponentType<{ className?: string }>;
  accept: string;
  file: File | null;
  disabled?: boolean;
  /** Returns the message to show when a file is refused, or null to accept it. */
  validate: (file: File) => string | null;
  onFile: (file: File) => void;
}

function formatSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * A file input that also takes drag and drop. The real input stays in the
 * label (visually hidden), so click, tap and keyboard all use the native
 * picker. Only files that pass `validate` reach `onFile`, so a refused file
 * leaves any earlier choice in place.
 */
export function FileDropZone({ label, noun, icon: Icon, accept, file, disabled = false, validate, onFile }: FileDropZoneProps) {
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function pick(picked: File) {
    const refusal = validate(picked);
    setError(refusal);
    if (!refusal) onFile(picked);
  }

  function onChange(e: ChangeEvent<HTMLInputElement>) {
    const picked = e.target.files?.[0];
    // Empty the input so choosing the same file again still fires a change,
    // since a drop can have replaced the file it last held.
    e.target.value = '';
    if (picked) pick(picked);
  }

  function onDragOver(e: DragEvent<HTMLLabelElement>) {
    if (disabled) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setDragging(true);
  }

  function onDragLeave(e: DragEvent<HTMLLabelElement>) {
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragging(false);
  }

  function onDrop(e: DragEvent<HTMLLabelElement>) {
    if (disabled) return;
    e.preventDefault();
    setDragging(false);
    const { files } = e.dataTransfer;
    if (files.length > 1) setError('Please drop one file at a time.');
    else if (files.length === 1) pick(files[0]);
  }

  const surface = dragging
    ? 'border-brand bg-brand-subtle dark:border-brand-bright dark:bg-brand/10'
    : 'border-ink-300 dark:border-white/10 hover:bg-ink-100 dark:hover:bg-white/5';

  return (
    <div className="mt-2">
      <label data-dragging={dragging} onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}
             className={`flex flex-col items-center gap-2 rounded-xl border-2 border-dashed px-4 py-5 text-center transition
                         has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-brand
                         ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} ${surface}`}>
        <input type="file" accept={accept} aria-label={label} disabled={disabled}
               className="sr-only" onChange={onChange} />
        <Icon className="h-8 w-8 text-ink-500 dark:text-ink-300" />
        <span aria-live="polite" className="max-w-full break-words text-[0.85rem] font-medium">
          {file ? `${file.name} (${formatSize(file.size)})` : null}
        </span>
        {/* Touch screens cannot drag a file in, so they only get the button. */}
        <span className="text-[0.85rem] text-ink-600 dark:text-ink-300 [@media(pointer:coarse)]:hidden">
          {file ? 'Drag and drop a different file here, or' : `Drag and drop ${noun} here, or`}
        </span>
        <span className="btn-ghost">{file ? 'Choose a different file' : 'Choose a file'}</span>
      </label>
      {error ? <p role="alert" className="text-status-error dark:text-red-400 text-[0.85rem] mt-2">{error}</p> : null}
    </div>
  );
}
